/**
 * Dispatch Suggestions Hooks
 *
 * Manages AI technician suggestions with owner approval workflow.
 * Fetches from job_assignment_suggestions table with scoring breakdown.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useCurrentTenant } from "./use-tenant";
import { toast } from "sonner";

// Query keys
export const DISPATCH_SUGGESTIONS_QUERY_KEY = ["dispatch-suggestions"];

export interface DispatchSuggestion {
  id: string;
  tenant_id: string;
  job_id: string;
  suggested_technician_id: string;
  confidence_score: number;
  reasoning: string;
  score_specialty: number;
  score_location: number;
  score_route: number;
  score_workload: number;
  alternative_suggestions: Array<{
    technician_id: string;
    technician_name: string;
    total_score: number;
    reasoning: string;
  }>;
  owner_approved: boolean | null;
  owner_approved_at: string | null;
  final_assigned_technician_id: string | null;
  owner_override_reason: string | null;
  created_at: string;
  expires_at: string;
  // Joined data
  jobs?: {
    customer_name: string;
    customer_phone: string;
    customer_address: string;
    service_type: string;
    scheduled_at: string;
    status: string;
  };
  technicians?: {
    name: string;
    phone: string | null;
    email: string | null;
  };
}

/**
 * Fetch all dispatch suggestions (pending, approved, rejected)
 */
export function useDispatchSuggestions(filters?: {
  pending_only?: boolean;
  approved_only?: boolean;
}) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...DISPATCH_SUGGESTIONS_QUERY_KEY, tenant?.id, filters],
    queryFn: async () => {
      if (!tenant?.id) throw new Error("No tenant context");

      let query = supabase
        .from("job_assignment_suggestions")
        .select(`
          *,
          jobs(customer_name, customer_phone, customer_address, service_type, scheduled_at, status),
          technicians(name, phone, email)
        `)
        .eq("tenant_id", tenant.id)
        .order("created_at", { ascending: false });

      if (filters?.pending_only) {
        query = query.is("owner_approved", null);
      } else if (filters?.approved_only === true) {
        query = query.eq("owner_approved", true);
      } else if (filters?.approved_only === false) {
        query = query.eq("owner_approved", false);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as DispatchSuggestion[];
    },
    enabled: !!tenant?.id,
    staleTime: 30000, // 30s cache
    refetchInterval: 60000, // Refetch every 60s for pending suggestions
  });
}

/**
 * Get count of pending suggestions (for nav badge)
 */
export function usePendingSuggestionsCount() {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...DISPATCH_SUGGESTIONS_QUERY_KEY, tenant?.id, "count", "pending"],
    queryFn: async () => {
      if (!tenant?.id) return 0;

      const { count, error } = await supabase
        .from("job_assignment_suggestions")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenant.id)
        .is("owner_approved", null);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!tenant?.id,
    staleTime: 20000, // 20s cache for badge
    refetchInterval: 30000, // Refetch every 30s
  });
}

/**
 * Approve a suggestion (assign technician to job)
 */
export function useApproveSuggestion() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: async (suggestionId: string) => {
      if (!tenant?.id) throw new Error("No tenant context");

      // Get suggestion details
      const { data: suggestion, error: suggestionError } = await supabase
        .from("job_assignment_suggestions")
        .select("*, jobs(id)")
        .eq("id", suggestionId)
        .eq("tenant_id", tenant.id)
        .single();

      if (suggestionError || !suggestion) throw new Error("Suggestion not found");

      // Update suggestion record (include tenant_id for multi-tenant isolation)
      const { error: updateError } = await supabase
        .from("job_assignment_suggestions")
        .update({
          owner_approved: true,
          owner_approved_at: new Date().toISOString(),
          final_assigned_technician_id: suggestion.suggested_technician_id,
        })
        .eq("id", suggestionId)
        .eq("tenant_id", tenant.id);

      if (updateError) throw updateError;

      // Assign technician to job (include tenant_id for multi-tenant isolation)
      const { error: jobError } = await supabase
        .from("jobs")
        .update({
          technician_id: suggestion.suggested_technician_id,
          status: "scheduled",
          assignment_suggestion_id: suggestionId,
          auto_assigned: false,
          assignment_method: "owner_approved",
        })
        .eq("id", suggestion.job_id)
        .eq("tenant_id", tenant.id);

      if (jobError) throw jobError;

      return { suggestionId, jobId: suggestion.job_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DISPATCH_SUGGESTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast.success("Technician assigned", {
        description: "Job has been assigned to the suggested technician",
      });
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error("Failed to approve suggestion:", error);
      toast.error("Failed to approve", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });
}

/**
 * Reject suggestion and reassign to different technician
 */
export function useReassignSuggestion() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      newTechnicianId,
      reason,
    }: {
      suggestionId: string;
      newTechnicianId: string;
      reason?: string;
    }) => {
      if (!tenant?.id) throw new Error("No tenant context");

      // Get suggestion details
      const { data: suggestion, error: suggestionError } = await supabase
        .from("job_assignment_suggestions")
        .select("*, jobs(id)")
        .eq("id", suggestionId)
        .eq("tenant_id", tenant.id)
        .single();

      if (suggestionError || !suggestion) throw new Error("Suggestion not found");

      // Update suggestion record (mark as rejected/overridden, include tenant_id for multi-tenant isolation)
      const { error: updateError } = await supabase
        .from("job_assignment_suggestions")
        .update({
          owner_approved: false,
          owner_approved_at: new Date().toISOString(),
          final_assigned_technician_id: newTechnicianId,
          owner_override_reason: reason || "Owner chose different technician",
        })
        .eq("id", suggestionId)
        .eq("tenant_id", tenant.id);

      if (updateError) throw updateError;

      // Assign chosen technician to job (include tenant_id for multi-tenant isolation)
      const { error: jobError } = await supabase
        .from("jobs")
        .update({
          technician_id: newTechnicianId,
          status: "scheduled",
          assignment_suggestion_id: suggestionId,
          auto_assigned: false,
          assignment_method: "manual",
        })
        .eq("id", suggestion.job_id)
        .eq("tenant_id", tenant.id);

      if (jobError) throw jobError;

      return { suggestionId, jobId: suggestion.job_id, newTechnicianId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DISPATCH_SUGGESTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast.success("Job reassigned", {
        description: "Job has been assigned to your chosen technician",
      });
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error("Failed to reassign:", error);
      toast.error("Failed to reassign", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });
}

/**
 * Get dispatch statistics for analytics
 */
export function useDispatchStats() {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...DISPATCH_SUGGESTIONS_QUERY_KEY, tenant?.id, "stats"],
    queryFn: async () => {
      if (!tenant?.id) return null;

      const { data, error } = await supabase
        .from("job_assignment_suggestions")
        .select("confidence_score, owner_approved, auto_assigned:jobs(auto_assigned)")
        .eq("tenant_id", tenant.id);

      if (error) throw error;

      const total = data.length;
      const pending = data.filter((s) => s.owner_approved === null).length;
      const approved = data.filter((s) => s.owner_approved === true).length;
      const rejected = data.filter((s) => s.owner_approved === false).length;
      const avgScore = total > 0
        ? Math.round(data.reduce((sum, s) => sum + s.confidence_score, 0) / total)
        : 0;

      return {
        total,
        pending,
        approved,
        rejected,
        approval_rate: total > 0 ? Math.round((approved / total) * 100) : 0,
        avg_score: avgScore,
      };
    },
    enabled: !!tenant?.id,
    staleTime: 60000, // 1 min cache for stats
  });
}
